import { type FormEvent, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type NotificationTemplateView, type NotificationTemplateChannel, NOTIFICATION_TEMPLATE_CHANNELS } from '@vello/api-client';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Field, Notice, PageHeader, Panel, Select, Table, TextArea, TextInput, FormRow, EmptyState, Stack, Text, PanelHead, Cluster, FormGrid } from '@vello/web-theme';

/**
 * Notificações (D318) — operator management of the **versioned, immutable** notification templates over the real
 * S095 surface: the published catalog (`GET /v1/notifications/templates`, filterable by key) and publishing a
 * version (`POST /v1/notifications/templates`, operator `notification:manage`). A published version can never be
 * rewritten (M11_FIX_08): re-sending identical content is a no-op, a changed body under an existing version is
 * refused and the operator publishes the next version instead. Templates are keyed by a language tag, never a
 * region — the region belongs to the country pack.
 */
function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export function NotificationsScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v, fail, language } = useVocab();
  const [filterKey, setFilterKey] = useState('');
  const [templates, setTemplates] = useState<readonly NotificationTemplateView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const readSeq = useRef(0);

  const [key, setKey] = useState('');
  const [locale, setLocale] = useState('');
  const [channel, setChannel] = useState<NotificationTemplateChannel>('PUSH');
  const [version, setVersion] = useState('1');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [variables, setVariables] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const k = filterKey.trim();
    const res = await runtime.notificationsAdmin.listTemplates(k === '' ? undefined : k);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setTemplates(res.value.templates);
      setError(undefined);
    } else {
      setTemplates((prev) => prev ?? []);
      setError(t('ntf.error.list', { code: fail(res.error) }));
    }
  }, [runtime, t, fail, filterKey]);

  // The catalog follows the typed key by itself (debounced ~400 ms; the first read is immediate) and stays current.
  const firstRead = useRef(true);
  useEffect(() => {
    const delay = firstRead.current ? 0 : 400;
    firstRead.current = false;
    const timer = globalThis.setTimeout(() => void load(), delay);
    return () => globalThis.clearTimeout(timer);
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue });

  const publish = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const v = Number.parseInt(version, 10);
    if (key.trim() === '' || locale.trim() === '' || body.trim() === '' || !Number.isInteger(v) || v <= 0) {
      setMsg({ tone: 'danger', text: t('ntf.invalid') });
      return;
    }
    setBusy(true);
    setMsg(undefined);
    const vars = variables.split(',').map((s) => s.trim()).filter((s) => s !== '');
    const res = await runtime.notificationsAdmin.publishTemplate({ key: key.trim(), locale: locale.trim(), channel, version: v, ...(subject.trim() !== '' ? { subject: subject.trim() } : {}), body, variables: vars });
    setBusy(false);
    if (res.ok) {
      setMsg({ tone: 'success', text: t(res.value.result === 'PUBLISHED' ? 'ntf.published' : 'ntf.unchanged', { key: res.value.key, version: String(res.value.version) }) });
      void load();
    } else if (res.error.code === 'CONFLICT') {
      setMsg({ tone: 'warning', text: t('ntf.conflict', { version: String(v) }) });
    } else {
      setMsg({ tone: 'danger', text: t('ntf.error.publish', { code: fail(res.error) }) });
    }
  };

  const useAsBase = (tpl: NotificationTemplateView): void => {
    setKey(tpl.key);
    setLocale(tpl.locale);
    setChannel((NOTIFICATION_TEMPLATE_CHANNELS as readonly string[]).includes(tpl.channel) ? (tpl.channel as NotificationTemplateChannel) : 'PUSH');
    setVersion(String(tpl.version + 1));
    setSubject(tpl.subject ?? '');
    setBody(tpl.body);
    setVariables(tpl.variables.join(', '));
    setMsg(undefined);
  };

  return (
    <>
      <PageHeader title={t('ntf.title')} subtitle={t('ntf.subtitle')} />

      <Panel>
        <Stack gap={4}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Stack gap={4}>
              <FormRow>
                <Field label={t('ntf.filter.key')} htmlFor="ntf-filter">
                  <TextInput id="ntf-filter" value={filterKey} placeholder={t('ntf.filter.key.placeholder')} onChange={(e) => setFilterKey(e.target.value)} />
                </Field>
              </FormRow>
            </Stack>
          </form>
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
        </Stack>
      </Panel>

      <Panel padded={false}>
        <PanelHead title={t('ntf.catalog.title')} />
        <Table
          columns={[
            { key: 'key', header: t('ntf.col.key'), render: (x: NotificationTemplateView) => <span className="tnum">{x.key}</span> },
            { key: 'channel', header: t('ntf.col.channel'), render: (x: NotificationTemplateView) => <Badge tone="neutral">{v('notification.channel', x.channel)}</Badge> },
            { key: 'locale', header: t('ntf.col.locale'), render: (x: NotificationTemplateView) => <span>{language(x.locale)}</span> },
            { key: 'version', header: t('ntf.col.version'), numeric: true, render: (x: NotificationTemplateView) => String(x.version) },
            { key: 'subject', header: t('ntf.col.subject'), render: (x: NotificationTemplateView) => x.subject ?? '—' },
            { key: 'vars', header: t('ntf.col.variables'), render: (x: NotificationTemplateView) => <span className="tnum">{x.variables.length > 0 ? x.variables.join(', ') : '—'}</span> },
            { key: 'body', header: t('ntf.col.body'), render: (x: NotificationTemplateView) => <span title={x.body}>{truncate(x.body, 60)}</span> },
            {
              key: 'act',
              header: '',
              render: (x: NotificationTemplateView) => (
                <Cluster justify="end">
                  <Button variant="ghost" onClick={() => useAsBase(x)}>{t('ntf.newVersion')}</Button>
                </Cluster>
              ),
            },
          ]}
          rows={templates ?? []}
          rowKey={(x) => `${x.key}:${x.channel}:${x.locale}:${x.version}`}
          loading={templates === undefined}
          loadingLabel={t('ui.loading')}
          empty={<EmptyState title={t('ntf.catalog.empty')} />}
        />
      </Panel>

      <Panel>
        <Stack gap={4}>
          <Text as="h2" role="title">{t('ntf.publish.title')}</Text>
          <Text as="p" role="caption" tone="secondary">{t('ntf.publish.hint')}</Text>
          <form onSubmit={(e) => void publish(e)} noValidate>
            <Stack gap={4}>
              <FormGrid>
                <Field label={t('ntf.field.key')} htmlFor="ntf-key">
                  <TextInput id="ntf-key" className="tnum" value={key} placeholder="ride_arriving" onChange={(e) => setKey(e.target.value)} required />
                </Field>
                <Field label={t('ntf.field.locale')} htmlFor="ntf-locale" hint={t('ntf.field.locale.hint')}>
                  <TextInput id="ntf-locale" className="tnum" value={locale} placeholder="pt" onChange={(e) => setLocale(e.target.value)} required />
                </Field>
                <Field label={t('ntf.field.channel')} htmlFor="ntf-channel">
                  <Select id="ntf-channel" value={channel} onChange={(e) => setChannel(e.target.value as NotificationTemplateChannel)}>
                    {NOTIFICATION_TEMPLATE_CHANNELS.map((c) => (
                      <option key={c} value={c}>{v('notification.channel', c)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label={t('ntf.field.version')} htmlFor="ntf-version">
                  <TextInput id="ntf-version" className="tnum" inputMode="numeric" value={version} onChange={(e) => setVersion(e.target.value)} required />
                </Field>
              </FormGrid>
              <Field label={t('ntf.field.subject')} htmlFor="ntf-subject">
                <TextInput id="ntf-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </Field>
              <Field label={t('ntf.field.body')} htmlFor="ntf-body" hint={t('ntf.field.body.hint')}>
                <TextArea id="ntf-body" value={body} onChange={(e) => setBody(e.target.value)} required />
              </Field>
              <Field label={t('ntf.field.variables')} htmlFor="ntf-vars" hint={t('ntf.field.variables.hint')}>
                <TextInput id="ntf-vars" className="tnum" value={variables} placeholder="eta, driverName" onChange={(e) => setVariables(e.target.value)} />
              </Field>
              {msg !== undefined && (
                <Notice tone={msg.tone}>{msg.text}</Notice>
              )}
              <Button type="submit" variant="primary" disabled={busy}>
                {busy ? t('ntf.publish.busy') : t('ntf.publish')}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Panel>
    </>
  );
}
